import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type ReactNode } from "react";

import {
  BULK_OPTION_DESCRIPTIONS,
  formatOptionCaption,
  type CopyTargetState,
  type OptionSummary,
  type WillUpdateCell,
} from "./bulkResolvePreview.helpers";
import {
  formatBulkTableRows,
  answerableRows,
  isBulkAnswer,
  isBulkSource,
  type BulkAnswer,
  type BulkResolveRow,
  type BulkSource,
} from "./bulkResolveWithValue.helpers";
import {
  formatFieldValue,
  getDiscrepancyStatus,
  getStatusLabel,
} from "./DiscrepancyReviewDialog.helpers";
import { EManifestBox18aCaption } from "./EManifestBox18aCaption";
import { type ResolveActionOption } from "./ResolveDiscrepanciesDialog";
import { CASCADE_HELPER, E_MANIFEST_HELPER, OPTION_BOX_SX, SaveWarnings } from "./ResolveQ1Options";
import { inactiveResultText, type ResolutionTargetLiteral } from "./resolveWithValue.helpers";
import { formatStatusAfterSave, type OutcomeCounts } from "./resolveWithValue.outcome";
import { ResultSection } from "./ResultSection";

/** Two columns of options, one column below 600px. */
const ANSWER_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
  gap: 1,
  mt: 0.5,
} as const;

// No vertical padding, so the control lines up with the label's first line
// and the caption lines sit below it instead of the control centring on both.
const TOP_ALIGNED_CONTROL_SX = { py: 0 } as const;

/** The scrollable table of every selected row: Manifest, Field, Line (when any row has one), each source's value, and what Save writes. */
export function BulkRowsTable({
  rows,
  willUpdateById,
}: {
  rows: readonly BulkResolveRow[];
  willUpdateById: ReadonlyMap<string, WillUpdateCell>;
}) {
  const tableRows = formatBulkTableRows(rows);
  const box18aById = new Map(rows.map((r) => [r.id, r.eManifestBox18a]));
  // Manifest-level fields (generator name, city…) have no waste line, so the
  // column only shows when at least one selected row is on a line.
  const showLine = rows.some((r) => r.wasteLineUnifiedId != null);
  return (
    <TableContainer sx={{ maxHeight: 240, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Manifest</TableCell>
            <TableCell>Field</TableCell>
            {showLine && <TableCell>Line</TableCell>}
            <TableCell>Document</TableCell>
            <TableCell>e-Manifest</TableCell>
            <TableCell>Vendor</TableCell>
            <TableCell>Will update</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row) => {
            const cell = willUpdateById.get(row.id);
            return (
              <TableRow key={row.id}>
                <TableCell>{row.manifest}</TableCell>
                <TableCell>{row.field}</TableCell>
                {showLine && <TableCell>{row.line}</TableCell>}
                <TableCell>{row.document}</TableCell>
                <TableCell>
                  {row.eManifest}
                  <EManifestBox18aCaption box18a={box18aById.get(row.id)} />
                </TableCell>
                <TableCell>{row.vendor}</TableCell>
                <TableCell sx={cell?.muted ? { color: "text.secondary" } : undefined}>
                  {cell?.text ?? "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface OptionDef {
  value: BulkAnswer;
  label: ReactNode;
  /** Extra reason the option is unavailable, shown in place of the count caption. */
  disabledReason?: string | null;
}

function AnswerOption(props: { def: OptionDef; summary: OptionSummary; loading: boolean }) {
  const { def, summary, loading } = props;
  const unavailable = def.disabledReason != null || !summary.selectable;
  return (
    <FormControlLabel
      value={def.value}
      disabled={loading || unavailable}
      sx={{ ...OPTION_BOX_SX, alignItems: "flex-start", pt: 1.25, pb: 1 }}
      control={<Radio size="small" sx={TOP_ALIGNED_CONTROL_SX} />}
      label={
        <Stack spacing={0}>
          <Typography variant="body2">{def.label}</Typography>
          {BULK_OPTION_DESCRIPTIONS[def.value] && (
            <Typography variant="caption" color="text.secondary">
              {BULK_OPTION_DESCRIPTIONS[def.value]}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {def.disabledReason ?? formatOptionCaption(summary)}
          </Typography>
        </Stack>
      }
    />
  );
}

interface BulkAnswerRadiosProps {
  rowCount: number;
  answer: BulkAnswer | null;
  loading: boolean;
  sourceDisabledReasons: Record<BulkSource, string | null>;
  cantDetermineOption?: ResolveActionOption;
  optionSummaries: Record<BulkAnswer, OptionSummary>;
  onSelectAnswer: (next: BulkAnswer) => void;
}

/** The single bulk question: which source is correct for every selected row, or dismiss/can't-determine. */
export function BulkAnswerRadios(props: BulkAnswerRadiosProps) {
  const { rowCount, answer, loading, sourceDisabledReasons, cantDetermineOption } = props;
  const defs: OptionDef[] = [
    {
      value: "DOCUMENT",
      label: "Use the document's value for all",
      disabledReason: sourceDisabledReasons.DOCUMENT,
    },
    {
      value: "EMANIFEST",
      label: "Use e-Manifest's value for all",
      disabledReason: sourceDisabledReasons.EMANIFEST,
    },
    {
      value: "VENDOR",
      label: "Use vendor's value for all",
      disabledReason: sourceDisabledReasons.VENDOR,
    },
    {
      value: "DISMISS",
      label: <strong>Mark as non-discrepancy</strong>,
    },
  ];
  if (cantDetermineOption) {
    defs.push({ value: "CANT_DETERMINE", label: <strong>Mark as blocked</strong> });
  }
  return (
    <Box>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {`What is the correct value for all ${rowCount}?`}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {CASCADE_HELPER}
      </Typography>
      <RadioGroup
        value={answer ?? ""}
        onChange={(e) => {
          if (isBulkAnswer(e.target.value)) props.onSelectAnswer(e.target.value);
        }}
      >
        <Box sx={ANSWER_GRID_SX}>
          {defs.map((def) => (
            <AnswerOption
              key={def.value}
              def={def}
              summary={props.optionSummaries[def.value]}
              loading={loading}
            />
          ))}
        </Box>
      </RadioGroup>
    </Box>
  );
}

// Every source that differs takes the picked value. e-Manifest is corrected
// by hand in RCRAInfo, so those rows wait in Fix pending.
const RESULT_ROWS: { target: ResolutionTargetLiteral; label: string; value: ValueOf }[] = [
  { target: "DOCUMENT", label: "Manifest document", value: (r) => r.manifestDocumentValue },
  { target: "EMANIFEST", label: "e-Manifest", value: (r) => r.eManifestValue },
  { target: "VENDOR_DATA", label: "Vendor", value: (r) => r.vendorValue },
];

type ValueOf = (row: BulkResolveRow) => string | null | undefined;

const ANSWER_VALUE: Record<BulkSource, ValueOf> = {
  EMANIFEST: (r) => r.eManifestValue,
  VENDOR: (r) => r.vendorValue,
  DOCUMENT: (r) => r.manifestDocumentValue,
};

const ANSWER_SOURCE_NAMES: Record<BulkSource, string> = {
  EMANIFEST: "e-Manifest",
  VENDOR: "vendor",
  DOCUMENT: "document",
};

/** The one value every row shares, formatted, or "Varies by row". */
function sharedOrVaries(rows: readonly BulkResolveRow[], value: ValueOf): string {
  const values = new Set(rows.map((r) => value(r) ?? null));
  if (values.size !== 1) return "Varies by row";
  const fields = new Set(rows.map((r) => r.field));
  return formatFieldValue(fields.size === 1 ? rows[0].field : "", [...values][0]);
}

function afterSaveCell(
  rows: readonly BulkResolveRow[],
  answer: BulkSource,
  { target, disabledReason }: CopyTargetState
): ReactNode {
  if (disabledReason != null) {
    return (
      <Typography variant="body2" color="text.secondary">
        No change ({disabledReason})
      </Typography>
    );
  }
  const shared = sharedOrVaries(rows, ANSWER_VALUE[answer]);
  const value = (
    <strong>
      {shared === "Varies by row" ? `Each row's ${ANSWER_SOURCE_NAMES[answer]} value` : shared}
    </strong>
  );
  if (target !== "EMANIFEST") return value;
  return (
    <>
      {value}
      <Typography variant="caption" color="text.secondary" display="block">
        {E_MANIFEST_HELPER}
      </Typography>
    </>
  );
}

/** "Preview": each source, its value now, and its value after save. */
export function BulkResultTable(props: {
  rows: readonly BulkResolveRow[];
  answer: BulkAnswer | null;
  /** Predicted statuses of the rows that will save. */
  outcomeCounts: OutcomeCounts;
  targets: readonly CopyTargetState[];
  /** Copies left disagreeing with the answer; shown, never blocking. */
  warnings: readonly string[];
}) {
  const { rows, answer, targets } = props;
  if (answer == null || !isBulkSource(answer)) {
    return (
      <ResultSection>
        <Typography variant="body2" color="text.secondary">
          {inactiveResultText(answer)}
        </Typography>
      </ResultSection>
    );
  }
  return (
    <ResultSection>
      <Table size="small" sx={{ "& td, & th": { px: 1, verticalAlign: "top" } }}>
        <TableHead>
          <TableRow>
            <TableCell>Source</TableCell>
            <TableCell>Current</TableCell>
            <TableCell>Update</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {RESULT_ROWS.map(({ target, label, value }) => {
            const state = targets.find((t) => t.target === target) ?? {
              target,
              disabledReason: null,
            };
            return (
              <TableRow key={target}>
                <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                <TableCell>{sharedOrVaries(rows, value)}</TableCell>
                <TableCell>{afterSaveCell(rows, answer, state)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Typography variant="body2" sx={{ mt: 1 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          Status:
        </Box>{" "}
        {currentStatusLabel(answerableRows(rows))} →{" "}
        <strong>{formatStatusAfterSave(props.outcomeCounts)}</strong>
      </Typography>
      <SaveWarnings warnings={props.warnings} />
    </ResultSection>
  );
}

/** The status the rows that will save are in now, or "Varies by row". */
function currentStatusLabel(rows: readonly BulkResolveRow[]): string {
  const statuses = new Set(rows.map((r) => getDiscrepancyStatus(r)));
  const [only] = statuses;
  if (statuses.size !== 1 || only == null) return "Varies by row";
  return getStatusLabel(only) ?? "—";
}
